/**
 * Module dependencies.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Combobox } from 'app/components/ui/combobox';
import { DataTable, DataTableGroup } from 'app/components/ui/data-table';
import { MultiSelectFilter } from 'app/components/ui/multi-select-filter';
import { Input } from 'app/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { InputField } from 'app/components/atoms/forms/fields/input/input-field';
import { SelectField } from 'app/components/atoms/forms/fields/select/select-field';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Pencil, PiggyBank, Plus, Search, Trash2 } from 'lucide-react';
import moment from 'moment';
import {
  CgTransaction,
  useCreateCryptoGestEntry,
  useCryptoGestTransactions,
  useCryptoGestTransferMatches,
  useCryptoGestWallets,
  useDeleteCryptoGestEntry,
  useEditCryptoGestEntry
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { amount, pct, signClass } from '../format';
import { useLivePrices } from '../use-live-prices';
import { useDisplayMoney } from '../use-display-money';
import { AssetIcon } from '../asset-icon';
import { PoolAllocationCell } from './crypto-gest-pool-allocation';
import { PiCgLink, usePoolInvestCryptoGestLinks } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import { useQueryState } from 'app/hooks/use-query-state';
import { CopyId } from 'app/components/atoms/copy-id/copy-id';

const ENTRY_TYPES = ['deposit', 'withdrawal', 'buy', 'sell', 'transfer_in', 'transfer_out', 'fee', 'reward', 'airdrop'];

/**
 * A ledger row enriched with live-derived values (in its trade currency).
 */

type TxRow = CgTransaction & {
  currentUsd: number | null;
  unitUsd: number | null;
  spentUsd: number | null;
  spentEur: number | null;
};

/**
 * Export `CryptoGestTransactions` component.
 *
 * The unified ledger across every wallet. Wallets are configured in the Wallets
 * section; this is where their transactions live.
 */

export function CryptoGestTransactions(): JSX.Element {
  const [translate] = useTranslation();
  const [walletFilter, setWalletFilter] = useQueryState('wallet', 'all');
  const [assetFilter, setAssetFilter] = useQueryState('asset', 'all');
  // Which entry types the ledger shows; `null` means "all" (nothing filtered).
  const [typeFilter, setTypeFilter] = useState<string[] | null>(null);
  const [poolFilter, setPoolFilter] = useQueryState('pool', 'all');
  const [missingParam, setMissingParam] = useQueryState('missing', '');
  const missingOnly = missingParam === '1';
  const setMissingOnly = (value: boolean) => setMissingParam(value ? '1' : '');
  const [search, setSearch] = useQueryState('q', '');
  const { data: wallets } = useCryptoGestWallets();
  const { data: transactions, isLoading } = useCryptoGestTransactions();
  const removeEntry = useDeleteCryptoGestEntry();
  const [adding, setAdding] = useState(false);
  const [matching, setMatching] = useState(false);
  const [editing, setEditing] = useState<TxRow | null>(null);
  const { format: usd, currency: displayCcy } = useDisplayMoney();

  // Crypto Gest → Pool Invest allocations (only when the user has Pool Invest).
  const hasPool = useFeature('poolInvest');
  const { data: cgLinks } = usePoolInvestCryptoGestLinks();
  const linksBySource = useMemo(() => {
    const map: Record<string, PiCgLink[]> = {};

    (cgLinks ?? []).forEach(link => {
      (map[link.sourceEntryId] ??= []).push(link);
    });

    return map;
  }, [cgLinks]);

  const livePrices = useLivePrices((transactions ?? []).map(entry => entry.symbol));

  const walletOptions = [
    { value: 'all', label: translate('cryptoGest.transactions.allWallets') },
    ...(wallets ?? []).map(wallet => ({ value: wallet.id, label: wallet.name }))
  ];

  // Assets present in the ledger (for the asset dropdown).
  const assetOptions = [
    { value: 'all', label: translate('cryptoGest.transactions.allAssets') },
    ...Array.from(new Set((transactions ?? []).map(entry => entry.symbol).filter(Boolean) as string[]))
      .sort()
      .map(symbol => ({ value: symbol, label: symbol }))
  ];

  // Type filter — only the types actually present, in canonical order.
  const presentTypes = new Set((transactions ?? []).map(entry => entry.type));
  const typeOptions = ENTRY_TYPES.filter(type => presentTypes.has(type)).map(type => ({
    value: type,
    label: translate(`cryptoGest.entryType.${type}`)
  }));
  const selectedTypes = typeFilter ?? typeOptions.map(option => option.value);
  const selectedTypeSet = new Set(selectedTypes);

  const poolOptions = [
    { value: 'all', label: translate('cryptoGest.transactions.poolAll') },
    { value: 'assigned', label: translate('cryptoGest.transactions.poolAssigned') },
    { value: 'split', label: translate('cryptoGest.transactions.poolSplit') },
    { value: 'none', label: translate('cryptoGest.transactions.poolNone') }
  ];

  const missingCount = (transactions ?? []).filter(entry => entry.needsData).length;

  // Wallet + asset + type + free-text search, applied client-side.
  const query = search.trim().toLowerCase();
  const filtered = (transactions ?? []).filter(entry => {
    if (walletFilter !== 'all' && entry.walletId !== walletFilter) {
      return false;
    }

    if (assetFilter !== 'all' && entry.symbol !== assetFilter) {
      return false;
    }

    if (!selectedTypeSet.has(entry.type)) {
      return false;
    }

    if (missingOnly && !entry.needsData) {
      return false;
    }

    if (poolFilter !== 'all') {
      const linkCount = linksBySource[entry.id]?.length ?? 0;

      if (poolFilter === 'assigned' && linkCount !== 1) {
        return false;
      }

      if (poolFilter === 'split' && linkCount < 2) {
        return false;
      }

      if (poolFilter === 'none' && linkCount !== 0) {
        return false;
      }
    }

    if (query) {
      const haystack = [
        entry.id,
        entry.symbol,
        entry.walletName,
        entry.type,
        translate(`cryptoGest.entryType.${entry.type}`),
        entry.amount,
        entry.note,
        moment(entry.timestamp).format('DD MMM YYYY, HH:mm'),
        ...(linksBySource[entry.id] ?? []).map(link => `${link.projectName} ${link.category}`)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!haystack.includes(query)) {
        return false;
      }
    }

    return true;
  });

  // Enrich each row with live-derived values in USD; the table formats them in
  // the user's chosen display currency. Rewards have no cost (unitUsd null).
  const rows: TxRow[] = filtered.map(entry => {
    const currentUsd =
      (entry.symbol ? livePrices[entry.symbol.toUpperCase()] : undefined) ?? entry.currentPriceUsd ?? null;
    const unitUsd = entry.unitPriceUsd && entry.unitPriceUsd > 0 ? entry.unitPriceUsd : null;
    const spentUsd = unitUsd != null ? parseFloat(entry.amount) * unitUsd : null;
    // Value in EUR at the transaction's own date (not today's FX).
    const spentEur =
      entry.unitPriceEurAt != null && entry.unitPriceEurAt > 0 ? parseFloat(entry.amount) * entry.unitPriceEurAt : null;
    return { ...entry, currentUsd, unitUsd, spentUsd, spentEur };
  });

  // Quantity totals across ALL filtered rows (every page): amount in vs out and
  // the net. Meaningful when filtered to one asset (mixed assets share no unit).
  const inQty = rows.filter(row => row.direction === 'in').reduce((sum, row) => sum + parseFloat(row.amount), 0);
  const outQty = rows.filter(row => row.direction === 'out').reduce((sum, row) => sum + parseFloat(row.amount), 0);
  const netQty = inQty - outQty;
  const symbolsInView = Array.from(new Set(rows.map(row => row.symbol)));
  const totalsSymbol = symbolsInView.length === 1 ? symbolsInView[0] : '';

  const columns = useMemo<ColumnDef<TxRow, any>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        enableSorting: false,
        cell: ({ row }) => <CopyId value={row.original.id} />,
        meta: { className: 'whitespace-nowrap' }
      },
      {
        id: 'asset',
        accessorFn: row => row.symbol,
        header: translate('cryptoGest.labels.asset'),
        cell: ({ row }) => (
          <span className={'flex items-center gap-2 font-medium text-foreground'}>
            <AssetIcon logo={row.original.logo} symbol={row.original.symbol} />

            {row.original.symbol}
          </span>
        )
      },
      {
        id: 'date',
        accessorFn: row => new Date(row.timestamp).getTime(),
        header: translate('cryptoGest.form.date'),
        cell: ({ row }) => moment(row.original.timestamp).format('DD MMM YYYY, HH:mm'),
        meta: { className: 'whitespace-nowrap text-xs text-muted-foreground' }
      },
      {
        accessorKey: 'walletName',
        header: translate('cryptoGest.transactions.wallet'),
        cell: info => info.getValue(),
        meta: { className: 'whitespace-nowrap text-foreground' }
      },
      {
        accessorKey: 'type',
        header: translate('cryptoGest.form.entryType'),
        cell: ({ row }) => (
          <span className={'inline-flex items-center gap-1.5 text-foreground'}>
            {row.original.direction === 'in' ? (
              <ArrowDownLeft className={'h-3.5 w-3.5 text-[#1B752B]'} />
            ) : (
              <ArrowUpRight className={'h-3.5 w-3.5 text-destructive'} />
            )}

            {translate(`cryptoGest.entryType.${row.original.type}`)}

            {row.original.counterSymbol && (
              <span className={'text-xs text-muted-foreground'}>
                {row.original.direction === 'in' ? '←' : '→'} {row.original.counterSymbol}
              </span>
            )}

            {row.original.needsData && (
              <Badge className={'border-amber-500/40 bg-amber-500/10 text-amber-700'} variant={'outline'}>
                {translate('cryptoGest.transactions.missingData')}
              </Badge>
            )}

            {(linksBySource[row.original.id] ?? []).map(link => (
              <span
                className={
                  'inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#c9d8f2] bg-[#eef2fb] px-2 py-0.5 text-[11px] font-medium text-[#2f5c9f]'
                }
                key={link.projectId}
              >
                <PiggyBank className={'h-3 w-3 shrink-0'} />
                {link.projectName}
              </span>
            ))}
          </span>
        ),
        meta: { className: 'whitespace-nowrap' }
      },
      {
        id: 'amount',
        accessorFn: row => parseFloat(row.amount),
        header: translate('cryptoGest.labels.amount'),
        cell: ({ row }) => `${amount(row.original.amount)} ${row.original.symbol}`,
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'unitPrice',
        accessorFn: row => row.unitUsd,
        header: translate('cryptoGest.form.unitPrice'),
        cell: ({ row }) => (row.original.unitUsd != null ? usd(row.original.unitUsd) : '—'),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        id: 'currentPrice',
        accessorFn: row => row.currentUsd,
        header: translate('cryptoGest.transactions.currentPrice'),
        cell: ({ row }) => (row.original.currentUsd != null ? usd(row.original.currentUsd) : '—'),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'total',
        accessorFn: row => row.spentUsd,
        header: translate('cryptoGest.transactions.total'),
        cell: ({ row }) => {
          const usdVal = row.original.spentUsd;
          const eurVal = row.original.spentEur;

          if (usdVal == null && eurVal == null) {
            return '—';
          }

          // Main value at the transaction's own date FX (EUR display); otherwise
          // the current-FX display value.
          const main =
            displayCcy === 'EUR' && eurVal != null
              ? `${eurVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
              : usdVal != null
              ? usd(usdVal)
              : '—';

          return (
            <span className={'flex flex-col items-end leading-tight'}>
              <span>{main}</span>

              {displayCcy !== 'USD' && usdVal != null ? (
                <span className={'text-xs text-muted-foreground'}>
                  {usdVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {' $'}
                </span>
              ) : null}
            </span>
          );
        },
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'profit',
        // Unrealized gain on a buy still held: (current − cost) × amount, in USD.
        accessorFn: row =>
          row.type === 'buy' && row.unitUsd != null && row.unitUsd > 0 && row.currentUsd != null
            ? (row.currentUsd - row.unitUsd) * parseFloat(row.amount)
            : null,
        header: translate('cryptoGest.transactions.profitValue'),
        cell: ({ row }) => {
          const { type, unitUsd, currentUsd } = row.original;

          if (type !== 'buy' || unitUsd == null || unitUsd <= 0 || currentUsd == null) {
            return '—';
          }

          const profitUsd = (currentUsd - unitUsd) * parseFloat(row.original.amount);
          const profitPct = (currentUsd / unitUsd - 1) * 100;

          return (
            <span className={`flex flex-col items-end leading-tight ${signClass(profitUsd)}`}>
              <span>{usd(profitUsd)}</span>

              <span className={'text-xs'}>{pct(profitPct)}</span>
            </span>
          );
        },
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className={'flex items-center justify-end gap-1'}>
            {row.original.source === 'manual' ? (
              <>
                <Button
                  className={'h-7 w-7'}
                  onClick={event => {
                    event.stopPropagation();
                    setEditing(row.original);
                  }}
                  size={'icon'}
                  variant={'ghost'}
                >
                  <Pencil className={'h-3.5 w-3.5 text-muted-foreground'} />
                </Button>

                <Button
                  className={'h-7 w-7'}
                  onClick={event => {
                    event.stopPropagation();
                    removeEntry.mutate({ walletId: row.original.walletId, entryId: row.original.id });
                  }}
                  size={'icon'}
                  variant={'ghost'}
                >
                  <Trash2 className={'h-3.5 w-3.5 text-muted-foreground'} />
                </Button>
              </>
            ) : null}

            {hasPool && <PoolAllocationCell links={linksBySource[row.original.id] ?? []} tx={row.original} />}
          </div>
        ),
        meta: { align: 'right' }
      }
    ],
    [translate, linksBySource, usd, displayCcy, hasPool, removeEntry]
  );

  // Fold consecutive reward payouts of the same asset in the same wallet into
  // one expandable line (keyed by wallet+asset so unrelated streaks stay apart).
  const rewardGroup = useMemo<DataTableGroup<TxRow>>(
    () => ({
      key: row => (row.type === 'reward' ? `reward:${row.walletId}:${row.symbol}` : null),
      summary: rows => {
        const first = rows[0];
        const totalAmount = rows.reduce((sum, row) => sum + parseFloat(row.amount), 0);
        const times = rows.map(row => new Date(row.timestamp).getTime());
        const from = moment(Math.min(...times)).format('DD MMM YYYY');
        const to = moment(Math.max(...times)).format('DD MMM YYYY');

        return {
          asset: (
            <span className={'flex items-center gap-2 font-medium text-foreground'}>
              <AssetIcon logo={first.logo} symbol={first.symbol} />

              {first.symbol}
            </span>
          ),
          date: (
            <span className={'text-xs text-muted-foreground'}>{translate('cryptoGest.group.range', { from, to })}</span>
          ),
          walletName: <span className={'text-foreground'}>{first.walletName}</span>,
          type: (
            <span className={'inline-flex items-center gap-1.5 text-foreground'}>
              <ArrowDownLeft className={'h-3.5 w-3.5 text-[#1B752B]'} />

              {translate('cryptoGest.labels.rewards')}

              <span className={'rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground'}>
                {translate('cryptoGest.group.rewards', { count: rows.length })}
              </span>
            </span>
          ),
          amount: `${amount(totalAmount)} ${first.symbol}`,
          unitPrice: '—',
          currentPrice: '—',
          total: '—',
          profit: '—'
        };
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-center justify-between gap-3'}>
        <Title>{translate('cryptoGest.transactions.title')}</Title>

        <div className={'flex items-center gap-2'}>
          <Button onClick={() => setMatching(true)} variant={'outline'}>
            <ArrowLeftRight className={'h-4 w-4'} />

            {translate('cryptoGest.transactions.matchTransfers')}
          </Button>

          <Button disabled={!wallets || wallets.length === 0} onClick={() => setAdding(true)}>
            <Plus className={'h-4 w-4'} />

            {translate('cryptoGest.transactions.add')}
          </Button>
        </div>
      </div>

      {/* Filters: free-text search + asset / wallet / type dropdowns */}
      <div className={'flex flex-wrap items-center gap-2'}>
        <div className={'relative min-w-[200px] flex-1'}>
          <Search
            className={'pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground'}
          />

          <Input
            className={'pl-8'}
            onChange={event => setSearch(event.target.value)}
            placeholder={translate('cryptoGest.transactions.searchPlaceholder')}
            value={search}
          />
        </div>

        <div className={'w-40'}>
          <Combobox onChange={value => setAssetFilter(value ?? 'all')} options={assetOptions} value={assetFilter} />
        </div>

        <div className={'w-44'}>
          <Combobox onChange={value => setWalletFilter(value ?? 'all')} options={walletOptions} value={walletFilter} />
        </div>

        <div className={'w-48'}>
          <MultiSelectFilter
            allLabel={translate('cryptoGest.filter.all')}
            clearLabel={translate('cryptoGest.filter.clear')}
            label={translate('cryptoGest.filter.types')}
            noneLabel={translate('cryptoGest.filter.none')}
            onChange={setTypeFilter}
            options={typeOptions}
            selectAllLabel={translate('cryptoGest.filter.selectAll')}
            summary={(count, total) => translate('cryptoGest.filter.count', { count, total })}
            value={selectedTypes}
          />
        </div>

        {hasPool && (
          <div className={'w-40'}>
            <Combobox onChange={value => setPoolFilter(value ?? 'all')} options={poolOptions} value={poolFilter} />
          </div>
        )}

        <Button onClick={() => setMissingOnly(!missingOnly)} variant={missingOnly ? 'default' : 'outline'}>
          {translate('cryptoGest.transactions.missingData')}
          {missingCount > 0 && ` (${missingCount})`}
        </Button>
      </div>

      {isLoading ? (
        <div className={'grid place-items-center py-20'}>
          <Spinner />
        </div>
      ) : !transactions || transactions.length === 0 ? (
        <div
          className={
            'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center'
          }
        >
          <div className={'text-base font-semibold text-foreground'}>
            {translate('cryptoGest.transactions.empty.title')}
          </div>

          <p className={'max-w-md text-sm text-muted-foreground'}>
            {translate('cryptoGest.transactions.empty.description')}
          </p>
        </div>
      ) : (
        <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={translate('cryptoGest.transactions.noResults')}
            group={rewardGroup}
            initialSorting={[{ id: 'date', desc: true }]}
            pageSize={25}
            rowClassName={row => ((linksBySource[row.id]?.length ?? 0) > 0 ? 'opacity-50' : '')}
          />

          {/* Quantity totals — only meaningful (and shown) for a single asset. */}
          {assetFilter !== 'all' && (
            <div
              className={
                'flex flex-wrap items-center justify-end gap-6 border-t border-border px-4 py-3 text-sm tabular-nums'
              }
            >
              <span className={'text-muted-foreground'}>
                {translate('cryptoGest.transactions.totalIn')}
                {':'}{' '}
                <span className={'font-medium text-emerald-600'}>
                  {'+'}
                  {amount(inQty)} {totalsSymbol}
                </span>
              </span>

              <span className={'text-muted-foreground'}>
                {translate('cryptoGest.transactions.totalOut')}
                {':'}{' '}
                <span className={'font-medium text-red-600'}>
                  {'−'}
                  {amount(outQty)} {totalsSymbol}
                </span>
              </span>

              <span className={'text-muted-foreground'}>
                {translate('cryptoGest.transactions.net')}
                {':'}{' '}
                <span className={'font-semibold text-foreground'}>
                  {amount(netQty)} {totalsSymbol}
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      <AddTransactionDialog
        defaultWalletId={walletFilter === 'all' ? '' : walletFilter}
        onOpenChange={setAdding}
        open={adding}
        wallets={(wallets ?? []).map(wallet => ({ id: wallet.id, name: wallet.name }))}
      />

      <EditTransactionDialog entry={editing} onOpenChange={open => !open && setEditing(null)} />

      <MatchTransfersDialog onOpenChange={setMatching} open={matching} />
    </div>
  );
}

/**
 * Dialog that scans every wallet (on-chain feed + ledger) for withdrawal↔deposit
 * pairs that look like internal transfers between the user's wallets, and lists
 * the matches. Detection runs on the server across all wallets.
 */

function MatchTransfersDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [translate] = useTranslation();
  const { data: matches, isLoading } = useCryptoGestTransferMatches(open);
  const rows = matches ?? [];

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className={'max-w-2xl'}>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.transactions.matchTransfers')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className={'grid place-items-center py-10'}>
            <Spinner />
          </div>
        ) : (
          <>
            <p className={'text-sm text-muted-foreground'}>
              {translate('cryptoGest.transactions.matchTransfersFound', { count: rows.length })}
            </p>

            {rows.length > 0 && (
              <div className={'max-h-[55vh] space-y-2 overflow-y-auto'}>
                {rows.map((match, index) => (
                  <div
                    className={
                      'flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm'
                    }
                    key={index}
                  >
                    <div className={'min-w-0'}>
                      <div className={'font-medium text-foreground'}>
                        {amount(match.from.amount)} {match.from.symbol}
                      </div>

                      <div className={'truncate text-xs text-muted-foreground'}>
                        {match.from.walletName}
                        {' · '}
                        {moment(match.from.timestamp).format('DD MMM YYYY')}
                      </div>
                    </div>

                    <ArrowLeftRight className={'h-4 w-4 shrink-0 text-muted-foreground'} />

                    <div className={'min-w-0 text-right'}>
                      <div className={'font-medium text-foreground'}>
                        {amount(match.to.amount)} {match.to.symbol}
                      </div>

                      <div className={'truncate text-xs text-muted-foreground'}>
                        {match.to.walletName}
                        {' · '}
                        {moment(match.to.timestamp).format('DD MMM YYYY')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className={'mt-2 flex justify-end'}>
          <Button onClick={() => onOpenChange(false)} variant={'outline'}>
            {translate('common.actions.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Edit-transaction dialog — fill/adjust a transaction (purchase value, type,
 * amount, date). Setting the unit price clears the "missing data" flag.
 */

export function EditTransactionDialog({
  entry,
  onOpenChange
}: {
  entry: CgTransaction | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const edit = useEditCryptoGestEntry();

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting }
  } = useForm({
    mode: 'all',
    defaultValues: { type: 'buy', amount: '', unitPriceFiat: '', total: '', currency: 'EUR', date: '', note: '' }
  });

  // Movements imported from a hard wallet (tracked/exchange) are read-only for
  // quantity/date/type — only the purchase value (cost basis) is editable.
  const readOnly = !!entry && entry.walletType !== 'manual';

  const numeric = (value: any) => {
    const parsed = parseFloat(value);

    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Keep amount × unit price = total in sync as the user types.
  const onUnitPriceChange = (value: any) => {
    const amountValue = numeric(getValues('amount'));
    const price = numeric(value);

    setValue('total', amountValue && price ? String(amountValue * price) : '');
  };

  const onTotalChange = (value: any) => {
    const amountValue = numeric(getValues('amount'));
    const total = numeric(value);

    setValue('unitPriceFiat', amountValue && total ? String(total / amountValue) : '');
  };

  const onAmountChange = (value: any) => {
    const amountValue = numeric(value);
    const price = numeric(getValues('unitPriceFiat'));

    setValue('total', amountValue && price ? String(amountValue * price) : '');
  };

  React.useEffect(() => {
    if (entry) {
      const amountValue = numeric(entry.amount);
      const price = entry.unitPriceFiat ? numeric(entry.unitPriceFiat) : 0;

      reset({
        type: entry.type,
        amount: entry.amount,
        unitPriceFiat: entry.unitPriceFiat ?? '',
        total: amountValue && price ? String(amountValue * price) : '',
        currency: entry.currency ?? 'EUR',
        date: entry.timestamp ? moment(entry.timestamp).format('YYYY-MM-DD') : '',
        note: entry.note ?? ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, reset]);

  const onSubmit = async (values: any) => {
    if (!entry) {
      return;
    }

    try {
      await edit.mutateAsync({
        walletId: entry.walletId,
        entryId: entry.id,
        type: values.type,
        amount: values.amount,
        unitPriceFiat: values.unitPriceFiat || null,
        currency: values.currency,
        timestamp: values.date ? new Date(values.date).toISOString() : undefined,
        note: values.note || null
      });

      success({ message: translate('cryptoGest.transactions.saved') });
      onOpenChange(false);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={!!entry}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate('cryptoGest.transactions.edit')}
            {entry?.symbol ? ` · ${entry.symbol}` : ''}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={'grid grid-cols-2 gap-x-4'}>
            <SelectField
              control={control}
              disabled={readOnly}
              label={translate('cryptoGest.form.entryType')}
              name={'type'}
              options={ENTRY_TYPES.map(type => ({ value: type, label: translate(`cryptoGest.entryType.${type}`) }))}
              rules={{ required: translate('common.errors.form.required') }}
            />

            <InputField
              control={control}
              disabled={readOnly}
              label={translate('cryptoGest.form.amount')}
              name={'amount'}
              onChange={onAmountChange}
              placeholder={'0.00'}
              rules={{ required: translate('common.errors.form.required') }}
              type={'number'}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.unitPrice')}
              name={'unitPriceFiat'}
              onChange={onUnitPriceChange}
              placeholder={'0.00'}
              rules={{}}
              type={'number'}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.totalInvested')}
              name={'total'}
              onChange={onTotalChange}
              placeholder={'0.00'}
              rules={{}}
              type={'number'}
            />

            <SelectField
              control={control}
              label={translate('cryptoGest.form.currency')}
              name={'currency'}
              options={[
                { value: 'EUR', label: translate('cryptoGest.settings.currency.EUR') },
                { value: 'USD', label: translate('cryptoGest.settings.currency.USD') }
              ]}
              rules={{ required: translate('common.errors.form.required') }}
            />

            <InputField
              control={control}
              disabled={readOnly}
              label={translate('cryptoGest.form.date')}
              name={'date'}
              rules={{}}
              type={'date'}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.note')}
              name={'note'}
              placeholder={translate('cryptoGest.form.notePlaceholder')}
              rules={{}}
            />
          </div>

          <div className={'mt-2 flex justify-end gap-2'}>
            <Button onClick={() => onOpenChange(false)} type={'button'} variant={'outline'}>
              {translate('common.actions.cancel')}
            </Button>

            <SubmitButton
              className={'mt-0'}
              disabled={isSubmitting}
              label={translate('common.actions.save')}
              loading={edit.isLoading}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add-transaction dialog (pick a wallet + trade currency, then the details).
 */

function AddTransactionDialog({
  wallets,
  defaultWalletId,
  open,
  onOpenChange
}: {
  wallets: Array<{ id: string; name: string }>;
  defaultWalletId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const create = useCreateCryptoGestEntry();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({
    mode: 'all',
    defaultValues: {
      walletId: defaultWalletId || wallets[0]?.id || '',
      symbol: '',
      type: 'buy',
      amount: '',
      unitPriceFiat: '',
      currency: 'EUR',
      date: moment().format('YYYY-MM-DD'),
      note: ''
    }
  });

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const onSubmit = async (values: any) => {
    try {
      await create.mutateAsync({
        walletId: values.walletId,
        symbol: values.symbol,
        type: values.type,
        amount: values.amount,
        unitPriceFiat: values.unitPriceFiat || undefined,
        currency: values.currency,
        timestamp: values.date ? new Date(values.date).toISOString() : undefined,
        note: values.note || undefined
      });

      success({ message: translate('common.actions.add') });
      close();
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={value => (value ? onOpenChange(true) : close())} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.transactions.add')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <SelectField
            control={control}
            label={translate('cryptoGest.transactions.wallet')}
            name={'walletId'}
            options={wallets.map(wallet => ({ value: wallet.id, label: wallet.name }))}
            rules={{ required: translate('common.errors.form.required') }}
          />

          <div className={'grid grid-cols-2 gap-x-4'}>
            <InputField
              control={control}
              label={translate('cryptoGest.form.coin')}
              name={'symbol'}
              placeholder={translate('cryptoGest.form.coinPlaceholder')}
              rules={{ required: translate('common.errors.form.required') }}
            />

            <SelectField
              control={control}
              label={translate('cryptoGest.form.entryType')}
              name={'type'}
              options={ENTRY_TYPES.map(type => ({ value: type, label: translate(`cryptoGest.entryType.${type}`) }))}
              rules={{ required: translate('common.errors.form.required') }}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.amount')}
              name={'amount'}
              placeholder={'0.00'}
              rules={{ required: translate('common.errors.form.required') }}
              type={'number'}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.unitPrice')}
              name={'unitPriceFiat'}
              placeholder={'0.00'}
              rules={{}}
              type={'number'}
            />

            <SelectField
              control={control}
              label={translate('cryptoGest.form.currency')}
              name={'currency'}
              options={[
                { value: 'EUR', label: translate('cryptoGest.settings.currency.EUR') },
                { value: 'USD', label: translate('cryptoGest.settings.currency.USD') }
              ]}
              rules={{ required: translate('common.errors.form.required') }}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.date')}
              name={'date'}
              rules={{}}
              type={'date'}
            />

            <InputField
              control={control}
              label={translate('cryptoGest.form.note')}
              name={'note'}
              placeholder={translate('cryptoGest.form.notePlaceholder')}
              rules={{}}
            />
          </div>

          <div className={'mt-2 flex justify-end gap-2'}>
            <Button onClick={close} type={'button'} variant={'outline'}>
              {translate('common.actions.cancel')}
            </Button>

            <SubmitButton
              className={'mt-0'}
              disabled={isSubmitting}
              label={translate('common.actions.add')}
              loading={create.isLoading}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
